import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  GetUstrExclusionsBIZcoreTD,
  WipeUstrExclusionsBIZcoreTD,
  FetchUstrExclusionsBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import UstrExclusionsModal from './UstrExclusionsModal';

import global from "../../../../../global.scss";

const UstrExclusionsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [exclusionsList, setExclusionsList] = useState([]);
  const [exclusionsTotal, setExclusionsTotal] = useState(0);

  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [fetchConfirmOpen, setFetchConfirmOpen] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "Id", sort: "id" },
    { label: "USITC HTS id", sort: "ref_usitc_hts_id" },
    { label: "Chapter 99 HTS", sort: "chapter_99_hts" },
    { label: "Exclusion type", sort: "exclusion_type" },
    { label: "Penalty rate", sort: "penalty_rate" },
    { label: "Active date", sort: "active_date" },
    { label: "Expiration date", sort: "expiration_date" },
    { label: "Time stamps", sort: "created_at" },
    { label: "", sort: null },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetExclusions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setExclusionsList(state.ustrExclusionsList.records || []);
      setExclusionsTotal(state.ustrExclusionsList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetExclusions(customLoading) {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetUstrExclusionsBIZcoreTD(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const handleWipe = () => {
    setWipeConfirmOpen(false);
    dispatch(WipeUstrExclusionsBIZcoreTD(setWiping)).then(() => {
      handleGetExclusions();
    });
  };

  const handleFetch = () => {
    setFetchConfirmOpen(false);
    dispatch(FetchUstrExclusionsBIZcoreTD(setFetching)).then(() => {
      handleGetExclusions();
    });
  };

  return (
    <Box>

      <UstrExclusionsModal
        open={modalOpen}
        setOpen={setModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetExclusions}
      />

      <ConfirmationModal
        open={wipeConfirmOpen}
        setOpen={setWipeConfirmOpen}
        title={"Wipe USTR Exclusions Table"}
        onCancel={() => setWipeConfirmOpen(false)}
        onConfirm={handleWipe}
      />

      <ConfirmationModal
        open={fetchConfirmOpen}
        setOpen={setFetchConfirmOpen}
        title={"Fetch USTR Exclusions Data"}
        onCancel={() => setFetchConfirmOpen(false)}
        onConfirm={handleFetch}
      />

      <TopBar
        title={"USTR Exclusions"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button3Text={wiping ? "Wiping..." : "Wipe table"}
        button3OnClick={() => setWipeConfirmOpen(true)}
        button2Text={fetching ? "Fetching..." : "Fetch data"}
        button2OnClick={() => setFetchConfirmOpen(true)}
        button1Text={"Add exclusion"}
        button1OnClick={() => {
          setUpdate({ defaultMode: 1 });
          setModalTitle("Add USTR exclusion");
          setModalOpen(!modalOpen);
        }}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          exclusionsList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No USTR exclusion found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sortDirection}
                        onClick={() => handleSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                exclusionsList.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.ref_usitc_hts_id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.chapter_99_hts || <NA />}</TableCell>
                    <TableCell>{row.exclusion_type || <NA />}</TableCell>
                    <TableCell>
                      {row.penalty_rate != null ? row.penalty_rate : <NA />}
                    </TableCell>
                    <TableCell>{row.active_date || <NA />}</TableCell>
                    <TableCell>{row.expiration_date || <NA />}</TableCell>
                    <TableCell>
                      <TableTimeStamps createdAt={row.created_at} />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.id || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light'] }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={exclusionsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

      <Menu
        open={menuOpen}
        anchorEl={menuAnchor}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit exclusion");
            setUpdate({ ...update, defaultMode: 2 });
            setMenuOpen(false);
            setModalOpen(!modalOpen);
          }}>
          <Box className="flexCenter_Row" gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>

    </Box>
  );
};

export default UstrExclusionsPage;
